import PrimaryInput from '@/components/ui/PrimaryInput';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from 'reactstrap';
import { IDeleteResponse } from '@/interfaces/common.model';
import { ITableData } from './MyWfh';
import Button from '@/components/ui/Button';
import { IShift, IShiftResponse } from '@/interfaces/Shits.model';
interface IAddWfhProps {
  isOpen: boolean;
  editData: ITableData | null;
  onClose: () => void;
  getMyWfhRequest: () => void;
}
interface IAddWfhForm {
  checkindate: string;
  checkoutdate: string;
  reason: string;
  fullDay: string;
  halfDayOption: string;
}
const AddWfhModal = ({
  isOpen,
  editData,
  onClose,
  getMyWfhRequest,
}: IAddWfhProps) => {
  const [shifts, setShifts] = useState<IShift[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isHalfday, setIsHalfday] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors,
    reset,
    setValue,
  } = useForm<IAddWfhForm>();
  useEffect(() => {
    getShifts();
  }, []);
  const getShifts = async () => {
    try {
      const response: IShiftResponse = await callApi(
        API_URL + API_ENDPOINT.getShift,
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        console.log(response);
        setShifts(response?.data?.shift);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  };
  const onSubmit = async (formData: IAddWfhForm) => {
    try {
      setIsLoading(true);

      const postData = {
        ...(editData && { wfhId: editData.id }),
        startDate: formData.checkindate,
        endDate: formData.checkoutdate,
        reason: formData.reason,
        ...(isHalfday
          ? {
              ...(formData?.halfDayOption === 'secondHalf'
                ? {
                    isHlafDayOdRequest: true,
                    wfhDuration: formData?.halfDayOption,
                    checkInTime: shifts?.find(
                      (shift) => shift.shiftName === 'Second-half',
                    )?.startTime,
                    checkOutTime: shifts?.find(
                      (shift) => shift.shiftName === 'Second-half',
                    )?.endTime,
                  }
                : {
                    isHlafDayOdRequest: true,
                    wfhDuration: formData?.halfDayOption,
                    checkInTime: shifts?.find(
                      (shift) => shift.shiftName === 'First-half',
                    )?.startTime,
                    checkOutTime: shifts?.find(
                      (shift) => shift.shiftName === 'First-half',
                    )?.endTime,
                  }),
            }
          : { wfhDuration: 'fullDay' }),
      };

      const endpoint =
        editData !== null
          ? API_URL + API_ENDPOINT.editWfh
          : API_URL + API_ENDPOINT.createWfh;

      const res: IDeleteResponse = await callApi(endpoint, {
        method: 'POST',
        data: postData,
      });

      if (res?.status) {
        showToastNotification('success', res?.message);
        reset();
        getMyWfhRequest();
        onClose();
      } else {
        showToastNotification('danger', res?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (editData) {
      setValue('checkindate', moment(editData.startDate).format('YYYY-MM-DD'));
      setValue('checkoutdate', moment(editData.endDate).format('YYYY-MM-DD'));
      setValue('reason', editData.reason ?? '');
    } else {
      reset({
        checkindate: moment().format('YYYY-MM-DD'),
        checkoutdate: moment().format('YYYY-MM-DD'),
        reason: '',
      });
    }
  }, [editData, isOpen]);

  return (
    <Modal
      className="modal-dialog modal-dialog-scrollable modal-dialog-centered"
      isOpen={isOpen}
      id="addWfhModal"
    >
      <div className="modal-content">
        <div className="modal-header p-4 border-0">
          <h1 className="modal-title fs-3 rajdhani-semibold">
            {editData ? 'Update WFH Request' : 'Apply for WFH'}
          </h1>
          <button
            type="button"
            className="btn-close"
            onClick={() => {
              reset();
              onClose();
            }}
          />
        </div>

        <div className="modal-body p-4">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-12 col-md-6 mb-3">
                <PrimaryInput
                  label="Check In Date"
                  required={true}
                  defaultValue={
                    watch('checkindate')
                      ? watch('checkindate')
                      : moment().format('YYYY-MM-DD')
                  }
                  type="date"
                  id="chkindate"
                  {...register('checkindate', {
                    required: 'Check-in date is required',
                    validate: (val) => {
                      if (moment(val).day() === 0) {
                        return 'Sunday is a non-working day. Please select another date.';
                      }
                      return (
                        moment(val).isSameOrAfter(
                          moment().startOf('year'),
                          'day',
                        ) ||
                        'Check-in date cannot be before Jan 1st of this year'
                      );
                    },
                  })}
                  min={moment().startOf('year').format('YYYY-MM-DD')}
                  onChange={(e) => {
                    setValue('checkindate', e.target.value);
                    setValue('checkoutdate', e.target.value);
                  }}
                  errors={errors?.checkindate?.message}
                />
              </div>
              <div className="col-12 col-md-6 mb-3">
                <PrimaryInput
                  label="Check Out Date"
                  required={true}
                  type="date"
                  {...register('checkoutdate', {
                    required: 'Check-out date is required',
                    validate: (val) => {
                      if (moment(val).day() === 0) {
                        return 'Sunday is a non-working day. Please select another date.';
                      }
                      return (
                        moment(val).isSameOrAfter(
                          moment(watch('checkindate')),
                          'day',
                        ) ||
                        'Check-out date must be same as or after start date'
                      );
                    },
                  })}
                  min={watch('checkindate')}
                  defaultValue={
                    watch('checkindate')
                      ? watch('checkindate')
                      : moment().format('YYYY-MM-DD')
                  }
                  errors={errors?.checkoutdate?.message}
                />
              </div>
              <div className="d-flex gap-2 gap-lg-2 mt-2 radio-color flex-wrap">
                <div className="form-group ">
                  <label className="d-flex gap-2 py-2 ">
                    <input
                      type="radio"
                      value="Full"
                      className="input-radio"
                      {...register('fullDay', {
                        onChange: () => {
                          setIsHalfday(false);
                          setValue('halfDayOption', '');
                        },
                      })}
                      checked={!isHalfday}
                    />
                    <span className="me-5 fs-4">Full</span>
                  </label>
                </div>

                <div className="form-group ">
                  <label className="d-flex gap-2 py-2 me-5">
                    <input
                      type="radio"
                      value="firstHalf"
                      className="input-radio"
                      {...register('halfDayOption', {
                        onChange: (e) => {
                          setIsHalfday(e.target.checked);
                        },
                        required: isHalfday
                          ? 'Please select First or Second Half.'
                          : false,
                      })}
                      checked={
                        isHalfday && watch('halfDayOption') === 'firstHalf'
                      }
                    />
                    <span className="me-5 fs-4">First Half</span>
                  </label>
                </div>

                <div className="form-group ">
                  <label className="d-flex gap-2 py-2">
                    <input
                      type="radio"
                      value="secondHalf"
                      className="input-radio"
                      {...register('halfDayOption', {
                        onChange: (e) => {
                          setIsHalfday(e.target.checked);
                        },
                      })}
                      checked={
                        isHalfday && watch('halfDayOption') === 'secondHalf'
                      }
                    />
                    <span className="fs-4">Second Half</span>
                  </label>
                </div>
              </div>

              {errors.halfDayOption && (
                <div className="form-text text-danger">
                  {errors.halfDayOption.message}
                </div>
              )}
              <div className="col-12 mb-3">
                <PrimaryInput
                  label="Reason"
                  required={true}
                  placeholder="Reason"
                  {...register('reason', {
                    required: 'Reason is required',
                  })}
                  errors={errors?.reason?.message}
                />
              </div>

              <div className="modal-footer p-2">
                <Button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    clearErrors();
                    onClose();
                    reset();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn btn-primary"
                  isLoading={isLoading}
                >
                  {editData ? 'Update WFH' : 'Apply WFH'}
                </Button>
              </div>
              {/* <div className="d-flex align-items-center justify-content-end gap-3 mt-4">
                <Button
                  type="button"
                  className="btn btn-secondary px-5"
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                >
                  Cancel
                </Button>

                <Button type="submit" className="btn btn-primary px-5">
                  {isLoading ? <Spinner /> : editData ? 'Update WFH' : 'Apply WFH'}
                </Button>
              </div> */}
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddWfhModal;
